import { apiClient } from '../client';
import { API_CONFIG } from '../config';
import { cachedConnectionCheck } from '../connectionCheckCache';

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price: string;
  currency: string;
  billing_period: string;
  features: any;
  type?: string | null;
  is_active: boolean;
  sort_order: number;
  trial_days?: number | null;
  razorpay_plan_id?: string | null;
  cashfree_plan_id?: string | null;
}

export interface GatewayConfig {
  gateways: Array<{ name: string; label: string }>;
  default: string;
}

export interface Subscription {
  id: string;
  organization_id: string;
  plan_id: string;
  status: string;
  /** Backend access verdict (same answer as the API gate). Authoritative when present. */
  has_access?: boolean;
  /** Derived state behind has_access: active | trialing | expired | cancelled | paused | pending | none. */
  access_state?: string;
  user_type?: string | null;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  trial_start?: string | null;
  trial_end?: string | null;
  trial_days?: number | null;
  razorpay_subscription_id?: string | null;
  plan?: SubscriptionPlan;
  /** Server-resolved billing figures. The only source of money on screen. */
  billing?: BillingTerms | null;
  /** "Renew Now" verdict, decided by the server (domain/billing/subscriptionRenewal.js). */
  renewal?: SubscriptionRenewal | null;
  entitlements?: {
    limits: Record<string, number>;
    features: Record<string, boolean>;
    sub_features?: Record<string, Record<string, number>>;
  };
  overrides?: Array<{
    key: string;
    value: any;
    type: string;
    reason?: string;
  }>;
  overrideEffects?: {
    limits?: Record<string, { planValue: number | null; overriddenValue: number }>;
    features?: Record<string, { planValue: boolean | null; overriddenValue: boolean }>;
    sub_features?: Record<string, Record<string, { planValue: number | null; overriddenValue: number }>>;
  };
}

/**
 * Whether the client can renew the current plan with a one-time payment, and
 * how urgent it is. The top banner, the Plans page and the Billing page all
 * read this one object — never re-derive it from dates on the client.
 */
export interface SubscriptionRenewal {
  eligible: boolean;
  reason:
    | 'eligible'
    | 'none'
    | 'not_paid_plan'
    | 'trialing'
    | 'pending'
    | 'cancelled'
    | 'paused'
    | 'no_period'
    | 'autopay'
    | 'outside_window';
  /** upcoming = 7–4 days left, due = 3–0 days left, expired = period over. */
  stage: 'none' | 'upcoming' | 'due' | 'expired';
  severity: 'notice' | 'warning' | 'critical' | null;
  days_left: number | null;
  period_end: string | null;
  renews_from: 'period_end' | 'today' | null;
  subscription_id: string | null;
  plan_id: string | null;
  plan_name: string | null;
  amount: number | null;
  currency: string;
  billing_period: string | null;
  window_days: number;
}

/**
 * What the customer is actually billed, resolved server-side.
 *
 * 🔴 The four amounts are NOT interchangeable, and the UI must not substitute
 * one for another:
 *   list_price    the plan's published price — never proof of what THIS
 *                 customer pays.
 *   agreed_amount what an admin recorded for this customer.
 *   last_payment  one past transaction. History, never a forecast.
 *   next_charge   what they'll be billed next. When `confidence` is
 *                 'assumed' it IS the list price standing in for an unknown,
 *                 and must be labelled as the list price rather than
 *                 presented as a next payment.
 */
export interface BillingAmount {
  amount: number;
  currency: string;
}

export interface BillingTerms {
  currency: string;
  list_price: BillingAmount | null;
  agreed_amount: BillingAmount | null;
  last_payment: (BillingAmount & { paid_at: string | null }) | null;
  next_charge:
    | (BillingAmount & {
        source: 'razorpay_subscription' | 'agreed_amount' | 'plan_list_price';
        confidence: 'known' | 'assumed';
      })
    | null;
  /**
   * Only present when the customer pays BELOW list. `kind` is the honesty
   * switch: 'offer' requires a verified Razorpay offer; anything else is a
   * 'negotiated' price and must not be called a promotion.
   */
  difference: {
    aboveListPrice: boolean;
    amount: number | null;
    percent: number | null;
    kind: 'offer' | 'negotiated' | null;
    offer: { id: string | null; name: string | null } | null;
  } | null;
  offer: { id: string | null; name: string | null; verified: boolean } | null;
  flags: {
    interval_mismatch: boolean;
    plan_deactivated: boolean;
    above_list_price: boolean;
    amount_unverified: boolean;
  };
}

export interface Payment {
  id: string;
  organization_id: string;
  subscription_id: string | null;
  razorpay_payment_id: string;
  razorpay_invoice_id?: string | null;
  amount: string;
  currency: string;
  status: string;
  payment_method: string | null;
  description: string | null;
  paid_at: string | null;
  created_at: string;
  plan_name?: string;
  plan_billing_period?: string;
  plan_period_start?: string;
  plan_period_end?: string;
}

export interface Addon {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_period: string;
  duration_days: number;
  features: any;
  is_active: boolean;
  razorpay_plan_id?: string;
}

export interface AddonSubscription {
  id: string;
  addon_id: string;
  organization_id: string;
  status: string;
  current_period_end: string;
  addon?: Addon | null;
  features?: unknown;
}

export interface UsageStats {
  contacts: number;
  messages: number;
  agents: number;
  templates: number;
  campaigns?: number;
  automations?: number;
  whatsapp_flows?: number;
  business_accounts?: number;
  contact_tags?: number;
  inbox?: number;
  commerce?: number;
  integrations?: number;
  connected_integrations?: string[];
  admins?: number;
  users?: number;
}

export const subscriptionsApi = {
  async getGatewayConfig(): Promise<{ data: GatewayConfig | null; error: string | null }> {
    try {
      return await cachedConnectionCheck('gateway-config', async () => {
        const response = await apiClient.get<GatewayConfig>('/subscriptions/gateway-config');
        if (response.error || !response.data) {
          throw new Error(response.error || 'Failed to fetch gateway config');
        }
        return { data: response.data, error: null };
      });
    } catch (error: any) {
      return { data: null, error: error.message || 'Failed to fetch gateway config' };
    }
  },

  async getPlans(): Promise<{ data: SubscriptionPlan[] | null; error: string | null }> {
    try {
      const response = await apiClient.get<SubscriptionPlan[]>('/subscriptions/plans');

      if (response.error) {
        return {
          data: null,
          error: response.error,
        };
      }

      if (!response.data) {
        return {
          data: null,
          error: 'No plans data received',
        };
      }

      const plans = Array.isArray(response.data) ? response.data : [];

      return {
        data: plans,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch subscription plans',
      };
    }
  },

  async getCurrentSubscription(): Promise<{ data: Subscription | null; error: string | null }> {
    try {
      const response = await apiClient.get<Subscription | null>('/subscriptions/current');

      if (response.error) {
        return {
          data: null,
          error: response.error,
        };
      }

      return {
        data: response.data ?? null,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch current subscription',
      };
    }
  },

  async getPaymentHistory(params?: { limit?: number; offset?: number }): Promise<{
    data: Payment[] | null;
    pagination: { limit: number; offset: number; total: number; has_more: boolean; next_offset: number | null } | null;
    error: string | null;
  }> {
    try {
      const response = await apiClient.get<Payment[]>('/subscriptions/payments', {
        params: {
          limit: params?.limit || 10,
          offset: params?.offset || 0,
        },
      });

      if (response.error || !response.data) {
        return {
          data: null,
          pagination: null,
          error: response.error || 'Failed to fetch payment history',
        };
      }

      return {
        data: Array.isArray(response.data) ? response.data : [],
        pagination: response.pagination || null,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        pagination: null,
        error: error.message || 'Failed to fetch payment history',
      };
    }
  },

  async createCheckoutSession(planId: string, gateway?: string): Promise<{ data: any | null; error: string | null; code?: string }> {
    try {
      const body: any = { planId };
      if (gateway) body.gateway = gateway;

      const response = await apiClient.post<any>('/subscriptions/create', body);

      if (response.error || !response.data) {
        return {
          data: null,
          error: response.error || 'Failed to create subscription',
          code: (response as any).code,
        };
      }

      return {
        data: response.data,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to create subscription',
      };
    }
  },

  /** POST /subscriptions/renew — one-time Razorpay order for the current plan. */
  async createRenewal(): Promise<{ data: any | null; error: string | null; code?: string }> {
    try {
      const response = await apiClient.post<any>('/subscriptions/renew', {});
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to start renewal', code: (response as any).code };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Failed to start renewal' };
    }
  },

  /** POST /subscriptions/renew/verify — confirm the renewal payment after checkout. */
  async verifyRenewal(body: {
    razorpayOrderId: string;
    razorpayPaymentId: string;
    razorpaySignature: string;
  }): Promise<{
    data: { applied: boolean; alreadyApplied: boolean; reason: string | null; current_period_end: string | null } | null;
    error: string | null;
    code?: string;
  }> {
    try {
      const response = await apiClient.post<any>('/subscriptions/renew/verify', body);
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to verify renewal', code: (response as any).code };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Failed to verify renewal' };
    }
  },

  /** This month's messages, all channels — separate because it is slower. */
  async getMessageUsage(): Promise<{
    data: { messages: number; messages_truncated: boolean } | null;
    error: string | null;
  }> {
    try {
      const response = await apiClient.get<{ messages: number; messages_truncated: boolean }>(
        '/subscriptions/usage/messages',
      );
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to fetch message usage' };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Failed to fetch message usage' };
    }
  },

  async getUsage(): Promise<{ data: UsageStats | null; error: string | null }> {
    try {
      const response = await apiClient.get<UsageStats>('/subscriptions/usage');

      if (response.error || !response.data) {
        return {
          data: null,
          error: response.error || 'Failed to fetch usage statistics',
        };
      }

      return {
        data: response.data,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch usage statistics',
      };
    }
  },

  async verifyPayment(params: { gateway?: string; razorpaySubscriptionId?: string; razorpayPaymentId?: string; razorpaySignature?: string; cashfreeOrderId?: string; cashfreePaymentId?: string }): Promise<{ success: boolean; error: string | null }> {
    try {
      const response = await apiClient.post<any>('/subscriptions/verify', params);
      return { success: !response.error, error: response.error || null };
    } catch (error: any) {
      return { success: false, error: error.message || 'Verification failed' };
    }
  },

  async checkSubscriptionStatus(subscriptionId: string): Promise<{ data: { id: string; status: string } | null; error: string | null }> {
    try {
      const response = await apiClient.get<{ id: string; status: string }>(`/subscriptions/status/${subscriptionId}`);
      return { data: response.data ?? null, error: response.error || null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Failed to check status' };
    }
  },

  async cancelSubscription(): Promise<{ success: boolean; error: string | null }> {
    try {
      const response = await apiClient.post<{ message: string }>('/subscriptions/cancel');

      if (response.error || !response.success) {
        return {
          success: false,
          error: response.error || 'Failed to cancel subscription',
        };
      }

      return {
        success: true,
        error: null,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to cancel subscription',
      };
    }
  },
  async downloadInvoice(paymentId: string, invoiceId?: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        return { success: false, error: 'Authentication token not found' };
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/subscriptions/invoice/${paymentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return { success: false, error: 'Failed to download invoice' };
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId || paymentId}.pdf`);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to download invoice',
      };
    }
  },

  async getAvailableAddons(): Promise<{ data: Addon[] | null; error: string | null }> {
    try {
      const response = await apiClient.get<Addon[]>('/addons');
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to fetch addons' };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  },

  async getActiveAddons(): Promise<{ data: AddonSubscription[] | null; error: string | null }> {
    try {
      const response = await apiClient.get<AddonSubscription[]>('/addons/active');
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to fetch active addons' };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  },

  async subscribeToAddon(addonId: string, gateway?: string): Promise<{ data: any; error: string | null; code?: string }> {
    try {
      const body: { addon_id: string; gateway?: string } = { addon_id: addonId };
      if (gateway) body.gateway = gateway;
      const response = await apiClient.post<any>('/addons/purchase', body);
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to subscribe to addon', code: (response as any).code };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  },
};
